import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, MapPin, Building2, Calendar, Layers,
  TrendingUp, Wrench, Users, Target, CheckCircle2, Briefcase,
  BarChart3, Lightbulb, ChevronRight, Shield, PieChart, Scale
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { trackRecord, calendlyLink } from '../../data/mock';
import { CaseStudySchema } from '../../components/StructuredData';

const NorthgreenAtriumPage = () => {
  // Get Northgreen property data from trackRecord
  const property = trackRecord.find(p => p.name.includes('Northgreen')) || trackRecord[2];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Property details
  const propertyDetails = {
    name: 'Northgreen Atrium Building',
    location: '1300 N Sam Houston Pkwy E, Houston, TX',
    submarket: 'North Houston / Sam Houston Parkway Corridor',
    county: 'Harris County',
    squareFeet: '75,000 SF',
    assetClass: 'Office',
    status: 'Active',
    image: property.image
  };

  // Acquisition rationale points
  const acquisitionRationale = [
    { title: 'Submarket Dynamics', description: 'Strategic location along the Sam Houston Parkway corridor with strong accessibility and visibility for tenants' },
    { title: 'Tenant Demand', description: 'Consistent demand from professional services, healthcare, and small business tenants seeking quality office space' },
    { title: 'Pricing Opportunity', description: 'Acquired at attractive basis relative to replacement cost with clear value creation potential' },
    { title: 'Portfolio Diversification', description: 'Office asset provides balance to multifamily-heavy portfolio and enhances overall risk-adjusted returns' }
  ];

  // Asset management strategies
  const assetManagementStrategies = [
    { title: 'Tenant Retention Programs', description: 'Proactive communication and responsive management to maintain high renewal rates' },
    { title: 'Lease Restructuring', description: 'Strategic negotiations to optimize rent escalations and lease terms' },
    { title: 'Broker Relationships', description: 'Active engagement with commercial brokers to maintain leasing pipeline' },
    { title: 'Tenant Mix Optimization', description: 'Targeting complementary tenants to create a balanced and stable tenant roster' }
  ];

  // Capital improvements
  const capitalImprovements = [
    { title: 'Building Systems', description: 'HVAC, electrical, and plumbing upgrades to ensure operational efficiency and tenant comfort' },
    { title: 'Common Area Upgrades', description: 'Lobby renovations, corridor improvements, and shared space enhancements' },
    { title: 'Curb Appeal', description: 'Exterior facade maintenance, landscaping, and signage improvements' },
    { title: 'Parking & Access', description: 'Parking lot maintenance, lighting, and accessibility improvements' }
  ];

  // Operational highlights
  const operationalHighlights = [
    { metric: 'Occupancy Strategy', description: 'Targeted leasing approach focused on tenant quality and lease duration' },
    { metric: 'Expense Optimization', description: 'Vendor renegotiations and operational efficiencies to reduce operating costs' },
    { metric: 'Tenant Mix', description: 'Diversified tenant base across professional services and healthcare sectors' },
    { metric: 'Lease Administration', description: 'Institutional-grade lease management and rent collection processes' }
  ];

  // Portfolio diversification benefits
  const diversificationBenefits = [
    'Office assets provide different cash flow characteristics than multifamily, balancing portfolio income',
    'Commercial tenants typically sign longer-term leases, providing income stability',
    'Different market drivers reduce correlation with residential rental market fluctuations',
    'Suburban office in strong corridors can offer attractive risk-adjusted returns'
  ];

  // Other case studies for navigation
  const otherCaseStudies = trackRecord.filter(p => !p.name.includes('Northgreen')).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Northgreen Atrium Case Study | Houston Office Investment | ASAP Capital</title>
        <meta 
          name="description" 
          content="Commercial real estate case study of Northgreen Atrium, a Houston office investment focused on value creation through active asset management." 
        />
        <meta name="keywords" content="Northgreen Atrium, Houston office building investment, commercial real estate case study, office investment property, value add commercial real estate, Texas commercial real estate investment" />
        <link rel="canonical" href="/case-studies/northgreen-atrium" />
      </Helmet>
      
      {/* Structured Data for SEO */}
      <CaseStudySchema caseStudy={{
        name: 'Northgreen Atrium Building',
        slug: 'northgreen-atrium',
        location: 'Houston, TX',
        type: 'Office',
        image: property?.image,
        description: 'Commercial real estate case study of Northgreen Atrium, a Houston office investment focused on value creation through active asset management.'
      }} />

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-0 lg:pt-40 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              {/* Breadcrumb */}
              <motion.div 
                className="flex items-center gap-2 text-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Link to="/case-studies" className="text-white/50 hover:text-[#D4AF37] transition-colors">
                  Case Studies
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
                <span className="text-white/70">Northgreen Atrium</span>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-10 items-end"
              >
                {/* Left - Content */}
                <div className="pb-10 lg:pb-16">
                  <motion.div 
                    className="flex items-center gap-3 mb-4"
                    variants={itemVariants}
                  >
                    <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-blue-500/20 text-blue-400 border-blue-500/30">
                      {propertyDetails.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs border border-white/10">
                      {propertyDetails.assetClass}
                    </span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-2"
                    variants={itemVariants}
                  >
                    Northgreen Atrium Office Investment Case Study
                  </motion.h1>

                  <motion.p 
                    className="text-lg text-[#D4AF37] mb-6"
                    variants={itemVariants}
                  >
                    Houston Office Building Investment
                  </motion.p>

                  <motion.div 
                    className="flex flex-wrap gap-6 text-white/60 text-sm mb-8"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      <span>Houston, TX</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-[#D4AF37]" />
                      <span>{propertyDetails.squareFeet}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#D4AF37]" />
                      <span>Sam Houston Pkwy Corridor</span>
                    </div>
                  </motion.div>

                  <motion.p 
                    className="text-white/50 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Past performance is not indicative of future results. Investment opportunities are offered only through a Private Placement Memorandum to investors.
                  </motion.p>
                </div>

                {/* Right - Image */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="relative rounded-t-2xl lg:rounded-2xl overflow-hidden">
                    <img 
                      src={propertyDetails.image}
                      alt={propertyDetails.name}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROPERTY OVERVIEW ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Building2 className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Property Overview
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { label: 'Property Name', value: propertyDetails.name, subValue: 'Class A Suburban Office', icon: Building2 },
                    { label: 'Location', value: 'Houston, TX', subValue: propertyDetails.submarket, icon: MapPin },
                    { label: 'Square Footage', value: propertyDetails.squareFeet, subValue: propertyDetails.assetClass, icon: Layers },
                    { label: 'Investment Status', value: propertyDetails.status, subValue: 'Active Asset Management', icon: Briefcase }
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                      variants={itemVariants}
                    >
                      <item.icon className="h-6 w-6 text-[#D4AF37] mb-3" />
                      <p className="text-white/40 text-xs mb-1">{item.label}</p>
                      <p className="text-white font-bold text-lg">{item.value}</p>
                      {item.subValue && <p className="text-white/50 text-sm">{item.subValue}</p>}
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 p-5 bg-white/5 rounded-xl border border-white/5"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/70 font-medium mb-1">Full Address</p>
                      <p className="text-white/50 text-sm">{propertyDetails.location}</p>
                      <p className="text-white/40 text-sm mt-1">{propertyDetails.county}, Greater Houston Metro Area</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET CONTEXT AND ACQUISITION RATIONALE ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Target className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Market Context and Acquisition Rationale
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  The Northgreen Atrium Building represents a strategic office acquisition along Houston's 
                  Sam Houston Parkway corridor. This property was identified through our disciplined 
                  acquisition process, which focuses on quality assets in established commercial nodes 
                  with strong fundamentals and value creation potential.
                </motion.p>

                <div className="grid md:grid-cols-2 gap-6">
                  {acquisitionRationale.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5 hover:border-blue-500/30 transition-all"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                        <CheckCircle2 className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                        <p className="text-white/50 text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ASSET MANAGEMENT AND LEASING STRATEGY ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Users className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Asset Management and Leasing Strategy
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Our asset management approach for Northgreen Atrium focuses on tenant retention, 
                  strategic leasing, and operational excellence to maximize property performance 
                  and long-term value creation.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {assetManagementStrategies.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 hover:border-emerald-500/30 transition-all"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                        <span className="text-emerald-400 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CAPITAL IMPROVEMENTS AND PROPERTY ENHANCEMENTS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                    <Wrench className="h-5 w-5 text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Capital Improvements and Property Enhancements
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Strategic capital improvements are designed to enhance tenant experience, improve 
                  operational efficiency, and maintain the property's competitive position within 
                  the North Houston office submarket.
                </motion.p>

                <div className="grid lg:grid-cols-2 gap-10">
                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Improvement Categories</h4>
                    <div className="space-y-4">
                      {capitalImprovements.map((item, index) => (
                        <div key={item.title} className="flex items-start gap-4 p-4 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5">
                          <span className="text-amber-400 font-bold text-sm mt-1">{String(index + 1).padStart(2, '0')}</span>
                          <div>
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-white/50 text-sm">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Value Enhancement Focus</h4>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10">
                      <ul className="space-y-3">
                        {[
                          'Maintain building systems to institutional standards',
                          'Create modern, professional common areas that attract quality tenants',
                          'Enhance energy efficiency to reduce operating costs',
                          'Improve exterior presentation and signage visibility',
                          'Ensure ADA compliance and accessibility throughout'
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <span className="text-white/60 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== OPERATIONAL PERFORMANCE AND STABILIZATION PLAN ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <BarChart3 className="h-5 w-5 text-purple-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Operational Performance and Stabilization Plan
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Our operational approach emphasizes disciplined property management, expense control, 
                  and strategic leasing to achieve stabilized performance and consistent cash flow.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {operationalHighlights.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5, borderColor: 'rgba(168, 85, 247, 0.3)' }}
                    >
                      <h4 className="text-white font-semibold mb-2">{item.metric}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Disclaimer */}
                <motion.div 
                  className="bg-white/[0.02] border border-white/5 rounded-xl p-4"
                  variants={itemVariants}
                >
                  <p className="text-white/40 text-xs text-center">
                    <Shield className="h-4 w-4 inline-block mr-2 text-white/30" />
                    Performance metrics shown are operational indicators and do not guarantee future results. 
                    All investments involve risk. Please refer to offering documents for complete details.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PORTFOLIO DIVERSIFICATION AND RISK MANAGEMENT ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                    <PieChart className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Portfolio Diversification and Risk Management
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  The Northgreen Atrium office investment plays a strategic role in portfolio construction, 
                  providing diversification benefits that complement our multifamily and retail holdings.
                </motion.p>

                <div className="grid lg:grid-cols-2 gap-6">
                  {diversificationBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5"
                      variants={itemVariants}
                    >
                      <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
                        <span className="text-cyan-400 font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-white/60">{benefit}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-cyan-500/20"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-4">
                    <Scale className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">Risk-Balanced Portfolio Construction</h4>
                      <p className="text-white/50 text-sm">
                        By including office assets alongside multifamily and retail investments, ASAP Capital 
                        constructs portfolios designed to balance cash flow stability with growth potential 
                        across different market cycles and economic conditions.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY FIT ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                      <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      How This Property Supports Our Investment Strategy
                    </h2>
                  </div>

                  <p className="text-white/60 mb-6">
                    Northgreen Atrium exemplifies our commitment to building diversified real estate 
                    portfolios in the Greater Houston market. By actively managing commercial office 
                    assets alongside our multifamily holdings, we seek to optimize risk-adjusted 
                    returns for our investors.
                  </p>

                  <p className="text-white/60 mb-6">
                    This investment demonstrates our ability to identify, acquire, and manage commercial 
                    real estate assets through our vertically integrated platform, applying the same 
                    disciplined approach we bring to all asset classes.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/strategy">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full">
                        View Investment Strategy
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full">
                        View Portfolio
                      </Button>
                    </Link>
                  </div>
                </motion.div>

                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6">Our Commercial Asset Approach</h4>
                  <div className="space-y-4">
                    {[
                      { title: 'Strategic Acquisition', description: 'Identify quality assets in established commercial corridors' },
                      { title: 'Active Management', description: 'Hands-on asset management focused on tenant retention and leasing' },
                      { title: 'Capital Discipline', description: 'Strategic improvements to enhance value without over-capitalization' },
                      { title: 'Portfolio Integration', description: 'Optimize overall portfolio risk-return through diversification' }
                    ].map((item, index) => (
                      <div key={item.title} className="flex items-start gap-4">
                        <span className="text-[#D4AF37] font-bold">{String(index + 1).padStart(2, '0')}</span>
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-white/50 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXPLORE MORE CTA ==================== */}
          <section className="py-20 lg:py-28 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Explore More Investment Case Studies
                </h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                  Review additional examples of our value-add investment approach across Houston's 
                  multifamily and commercial real estate markets.
                </p>
              </motion.div>

              {/* Other Case Studies */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {otherCaseStudies.map((study) => (
                  <motion.div
                    key={study.id}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[#D4AF37] text-sm mb-1">{study.type}</p>
                      <h4 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {study.name}
                      </h4>
                      <p className="text-white/50 text-sm">{study.location}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <Link to="/case-studies">
                  <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20">
                    View All Case Studies
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full">
                    View Portfolio
                  </Button>
                </Link>
                <motion.a 
                  href={calendlyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-3 h-12 rounded-full">
                    Schedule Consultation
                    <Calendar className="ml-2 h-4 w-4" />
                  </Button>
                </motion.a>
              </motion.div>
            </div>
          </section>

          {/* ==================== BACK LINK ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <Link 
                to="/case-studies"
                className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Case Studies
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default NorthgreenAtriumPage;
